import { BuildingAreaType_Enum } from "@/graphql";

import type { BuildingUseType } from "../types/BuildingUseType";
import { grossArea } from "./buildingAreaTable";

/** A project_building_area row as Hasura answers it: one above- and one below-ground row per building use. */
export interface StoredBuildingAreaRow {
  buildingTypeId?: number | null;
  buildingAreaType: BuildingAreaType_Enum | string;
  area: unknown;
  floors?: number | null;
  heatedArea: unknown;
}

/** One area type's figure over every use. */
export interface BuildingAreaSum {
  area: number;
  floors: number;
  heatedArea: number;
}

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/** Areas and heated areas summed over every use, the largest floor count; null when no row has the type. */
export const sumBuildingAreas = (
  rows: readonly StoredBuildingAreaRow[] | null | undefined,
  type: BuildingAreaType_Enum
): BuildingAreaSum | null => {
  const ofType = (rows ?? []).filter((row) => row.buildingAreaType === type);
  if (ofType.length === 0) return null;
  return {
    area: ofType.reduce((sum, row) => sum + (toNumber(row.area) ?? 0), 0),
    floors: Math.max(...ofType.map((row) => row.floors ?? 0)),
    heatedArea: ofType.reduce((sum, row) => sum + (toNumber(row.heatedArea) ?? 0), 0),
  };
};

// The rows as the table's uses, largest gross area first, ties to the project's own type, then the lower id.
// Fewer than two types is a single use, which the table reads from the project-level fields, so [].
export const toBuildingUses = (
  rows: readonly StoredBuildingAreaRow[] | null | undefined,
  projectBuildingTypeId: number | null = null
): BuildingUseType[] => {
  const byType = new Map<number, BuildingUseType>();
  for (const row of rows ?? []) {
    const typeId = row.buildingTypeId ?? projectBuildingTypeId;
    if (typeId === null) continue;
    const use: BuildingUseType = byType.get(typeId) ?? { buildingTypeId: typeId, area: 0 };
    if (row.buildingAreaType === BuildingAreaType_Enum.above_ground) {
      use.areaAboveGround = toNumber(row.area);
      use.floorsAboveGround = row.floors ?? null;
      use.heatedAreaAboveGround = toNumber(row.heatedArea);
    } else {
      use.areaBelowGround = toNumber(row.area);
      use.floorsBelowGround = row.floors ?? null;
      use.heatedAreaBelowGround = toNumber(row.heatedArea);
    }
    byType.set(typeId, use);
  }
  if (byType.size < 2) return [];

  const isProjectType = (use: BuildingUseType) => (use.buildingTypeId === projectBuildingTypeId ? 0 : 1);
  return [...byType.values()]
    .map((use) => ({
      buildingTypeId: use.buildingTypeId,
      area: grossArea(use),
      areaAboveGround: use.areaAboveGround ?? null,
      floorsAboveGround: use.floorsAboveGround ?? null,
      heatedAreaAboveGround: use.heatedAreaAboveGround ?? null,
      areaBelowGround: use.areaBelowGround ?? null,
      floorsBelowGround: use.floorsBelowGround ?? null,
      heatedAreaBelowGround: use.heatedAreaBelowGround ?? null,
    }))
    .sort(
      (left, right) =>
        grossArea(right) - grossArea(left) ||
        isProjectType(left) - isProjectType(right) ||
        (left.buildingTypeId ?? 0) - (right.buildingTypeId ?? 0)
    );
};
